import React, { useState } from 'react';
import Button from './Button';

const plans = [
  {
    id: 'logo',
    tag: 'BRAND IDENTITY',
    title: 'Logo Design',
    oneTime: true,
    oneTimePrice: 54.99,
  },
  {
    id: 'essential',
    tag: 'ESSENTIAL BASE',
    title: 'Core Website',
    setup: 299,
    monthlyPrice: 59,
    yearlyPrice: 588,
  },
  {
    id: 'pro',
    tag: 'MOST POPULAR',
    title: 'Pro Website',
    setup: 499,
    monthlyPrice: 99,
    yearlyPrice: 996,
  },
  {
    id: 'scale',
    tag: 'WORK SMARTER',
    title: 'Automation',
    setup: 499,
    monthlyPrice: 149,
    yearlyPrice: 1428,
  }
];

const PayPage = ({ navigate }) => {
  const initialPlanId = plans.some(p => p.id === window.history.state?.plan)
    ? window.history.state.plan
    : 'pro';
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const isOneTime = !!selectedPlan.oneTime;
  const recurringAmount = isOneTime ? 0 : (billingPeriod === 'yearly' ? selectedPlan.yearlyPrice : selectedPlan.monthlyPrice);
  const downPayment = isOneTime ? selectedPlan.oneTimePrice : Math.round(selectedPlan.setup * 0.2);
  const remainingSetup = isOneTime ? 0 : selectedPlan.setup - downPayment;

  const handleCheckout = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          isOneTime
            ? {
                planName: selectedPlan.title,
                paymentType: 'one_time',
              }
            : {
                planName: selectedPlan.title,
                setupFee: selectedPlan.setup,
                recurringFee: recurringAmount,
                billingPeriod: billingPeriod,
              }
        ),
      });

      const session = await response.json();

      if (response.ok && session.url) {
        window.location.href = session.url; // Redirect to Stripe Checkout
      } else {
        setError(session.error || 'Failed to create checkout session. Ensure you have set STRIPE_SECRET_KEY in your .env.local file.');
        setIsProcessing(false);
      }
    } catch (err) {
      setError('Network error. Please make sure the server is running.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="py-32 max-w-[95vw] mx-auto px-4 md:px-8 min-h-screen flex flex-col bg-background">
      <div className="max-w-5xl mx-auto w-full mt-12 mb-20 flex-grow">
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter leading-none mb-4 text-foreground text-center" style={{ fontFamily: "'Poppins Black', sans-serif" }}>
          SECURE CHECKOUT
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground text-center mb-16 max-w-2xl mx-auto">
          {isOneTime
            ? 'Complete your one-time payment to kickstart your project. No subscription, no recurring charges.'
            : 'Complete your payment to kickstart your project. We require a 20% down payment today, with the remainder billed upon fulfillment.'}
        </p>

        {/* Plan Selection Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left Column: Selection (Takes up 3/5) */}
          <div className="lg:col-span-3 space-y-12">
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tighter mb-6 text-foreground flex items-center gap-3">
                <span className="bg-accent text-accent-foreground w-8 h-8 flex items-center justify-center rounded-full text-sm">1</span> 
                Select Your Plan
              </h2>
              <div className="space-y-4">
                {plans.map((plan) => (
                  <div 
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`p-5 md:p-6 border-2 cursor-pointer transition-all duration-200 ${selectedPlanId === plan.id ? 'border-accent bg-accent/5' : 'border-border bg-muted/30 hover:border-foreground/50 hover:bg-muted/50'}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <div className="font-bold uppercase tracking-widest text-foreground text-lg">{plan.title}</div>
                          {plan.id === 'pro' && (
                            <span className="bg-accent text-accent-foreground text-[10px] uppercase tracking-widest font-black px-2 py-0.5">MOST POPULAR</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground font-medium">
                          {plan.oneTime ? `One-Time Payment: $${plan.oneTimePrice}` : `Setup Fee: $${plan.setup}`}
                        </div>
                      </div>
                      <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex flex-shrink-0 items-center justify-center ${selectedPlanId === plan.id ? 'border-accent' : 'border-muted-foreground'}`}>
                        {selectedPlanId === plan.id && <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-accent"></div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!isOneTime && (
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tighter mb-6 text-foreground flex items-center gap-3">
                <span className="bg-accent text-accent-foreground w-8 h-8 flex items-center justify-center rounded-full text-sm">2</span>
                Billing Cycle
              </h2>
              <div className="flex border-2 border-border p-1 bg-muted/30 select-none">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`flex-1 px-4 py-4 uppercase tracking-tighter font-bold transition-all duration-200 text-sm md:text-base ${
                    billingPeriod === 'monthly'
                      ? 'bg-accent text-accent-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Bill Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('yearly')}
                  className={`flex-1 px-4 py-4 uppercase tracking-tighter font-bold transition-all duration-200 text-sm md:text-base ${
                    billingPeriod === 'yearly'
                      ? 'bg-accent text-accent-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Bill Annually <span className="hidden md:inline">(Save 20%)</span>
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Right Column: Order Summary (Takes up 2/5) */}
          <div className="lg:col-span-2">
            <div className="sticky top-32">
              <h2 className="text-2xl font-bold uppercase tracking-tighter mb-6 text-foreground flex items-center gap-3">
                <span className="bg-accent text-accent-foreground w-8 h-8 flex items-center justify-center rounded-full text-sm">3</span> 
                Order Summary
              </h2>
              <div className="border-2 border-border bg-muted/30 p-8 space-y-8">
                <div>
                  <h3 className="font-bold text-xl uppercase tracking-wide text-foreground mb-1">{selectedPlan.title}</h3>
                  <p className="text-sm text-accent font-bold uppercase tracking-widest">
                    {isOneTime ? 'One-Time Payment' : `${billingPeriod === 'yearly' ? 'Annual' : 'Monthly'} Subscription`}
                  </p>
                </div>

                {isOneTime ? (
                  <div className="space-y-4 pt-6 border-t-2 border-border">
                    <div className="flex justify-between text-foreground font-medium">
                      <span>{selectedPlan.title}</span>
                      <span>${selectedPlan.oneTimePrice}</span>
                    </div>
                    <div className="text-xs text-muted-foreground italic leading-relaxed">
                      * One-time payment. No subscription, no recurring charges.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 pt-6 border-t-2 border-border">
                      <div className="flex justify-between text-foreground font-medium">
                        <span>Total Setup Fee</span>
                        <span>${selectedPlan.setup}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground text-sm pl-4">
                        <span>- Down Payment (20%)</span>
                        <span className="text-foreground">${downPayment}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground text-sm pl-4">
                        <span>- Remaining Balance<br/><span className="text-[10px] tracking-wide uppercase">(Billed upon fulfillment)</span></span>
                        <span>${remainingSetup}</span>
                      </div>
                    </div>

                    <div className="space-y-2 pt-6 border-t-2 border-border">
                      <div className="flex justify-between text-foreground font-medium">
                        <span>Recurring Fee ({billingPeriod})</span>
                        <span>${recurringAmount}</span>
                      </div>
                      <div className="text-xs text-muted-foreground italic leading-relaxed">
                        * The first recurring charge of ${recurringAmount} will start in exactly 30 days.
                      </div>
                    </div>
                  </>
                )}

                <div className="pt-8 border-t-2 border-border">
                  <div className="flex justify-between items-end mb-8">
                    <span className="font-bold uppercase tracking-widest text-foreground">Due Today</span>
                    <span className="text-5xl font-black tracking-tighter text-foreground leading-none">${downPayment}</span>
                  </div>
                  
                  {error && (
                    <div className="bg-red-900/20 border border-red-500/50 p-4 mb-6">
                      <p className="text-red-400 text-sm font-bold">{error}</p>
                    </div>
                  )}

                  <Button 
                    size="large" 
                    className="w-full" 
                    onClick={handleCheckout}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'PROCESSING...' : 'PROCEED TO CHECKOUT'}
                  </Button>
                  <div className="text-center mt-6 flex items-center justify-center gap-2 text-muted-foreground">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 19.93c-4.04-1.12-7-5.32-7-9.93V6.3l7-3.11 7 3.11v4.63c0 4.61-2.96 8.81-7 9.93zm-1-10.43V8h2v2.5h-2zm0 3.5v-2h2v2h-2z"/></svg>
                    <p className="text-[10px] font-bold uppercase tracking-widest">Payments Secured by Stripe</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPage;
